import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule, DatePipe } from '@angular/common';
import { authBackendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogData } from '../practitioner-list/confirm-action-dialog.component';

export type SystemAdminType = 'SuperAdmin' | 'EventManager';

export interface SuperAdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneNumber?: string;
  isApproved: boolean;
  createdAt: string;
  lastLoginAt?: string;
  status: 'Active' | 'Deactivated';
  accountType?: SystemAdminType; // may be absent on older callers — defaulted below
}

@Component({
  selector: 'app-superadmin-details',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    CommonModule,
    DatePipe
  ],
  templateUrl: './superadmin-details.component.html',
  styleUrl: './superadmin-details.component.scss'
})
export class SuperAdminDetailsComponent {
  http = inject(HttpClient);
  snackBar = inject(MatSnackBar);
  backendApi = inject(authBackendEndPointToken);
  errorService = inject(ErrorService);
  dialog = inject(MatDialog);
  dialogRef = inject(MatDialogRef<SuperAdminDetailsComponent>);

  user: SuperAdminUser;

  constructor(@Inject(MAT_DIALOG_DATA) public data: SuperAdminUser) {
    // Default to SuperAdmin for callers that haven't been updated to tag accountType yet
    this.user = { accountType: 'SuperAdmin', ...data };
  }

  get isEventManager(): boolean {
    return this.user.accountType === 'EventManager';
  }

  get typeLabel(): string {
    return this.isEventManager ? 'Global Event Manager' : 'Super Admin';
  }

  private get baseEndpoint(): string {
    return this.isEventManager
      ? `${this.backendApi}/api/Admin/global-event-managers/${this.user.id}`
      : `${this.backendApi}/api/SuperAdmin/users/${this.user.id}`;
  }

  deactivateUser() {
    const dialogData: ConfirmActionDialogData = {
      title: `Deactivate ${this.typeLabel}`,
      message: `Are you sure you want to deactivate this ${this.typeLabel} user? They will lose access to the system.`,
      actionType: 'archive',
      itemName: this.user.fullName,
      requiresReason: true,
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel'
    };

    const confirmDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDeactivate(result.reason || 'No reason provided');
      }
    });
  }

  private performDeactivate(reason: string) {
    this.http.post(`${this.baseEndpoint}/deactivate`, { reason })
      .subscribe({
        next: () => {
          this.snackBar.openFromComponent(SuccessMessageComponent, {
            data: { message: `${this.typeLabel} user deactivated successfully` },
            duration: 3000,
          });
          this.dialogRef.close({ updated: true });
        },
        error: (err) => {
          console.error('Deactivate error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to deactivate user'
          );
        }
      });
  }

  reactivateUser() {
    const dialogData: ConfirmActionDialogData = {
      title: `Reactivate ${this.typeLabel}`,
      message: `Are you sure you want to reactivate this ${this.typeLabel} user? They will regain access.`,
      actionType: 'reactivate',
      itemName: this.user.fullName,
      requiresReason: false,
      confirmButtonText: 'Reactivate',
      cancelButtonText: 'Cancel'
    };

    const confirmDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performReactivate();
      }
    });
  }

  private performReactivate() {
    this.http.post(`${this.baseEndpoint}/reactivate`, {})
      .subscribe({
        next: () => {
          this.snackBar.openFromComponent(SuccessMessageComponent, {
            data: { message: `${this.typeLabel} user reactivated successfully` },
            duration: 3000,
          });
          this.dialogRef.close({ updated: true });
        },
        error: (err) => {
          console.error('Reactivate error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to reactivate user'
          );
        }
      });
  }

  deleteUser() {
    const dialogData: ConfirmActionDialogData = {
      title: `Delete ${this.typeLabel}`,
      message: this.isEventManager
        ? `This will revoke the Global Event Manager role from this user. Are you sure?`
        : `⚠️ WARNING: This action is PERMANENT and cannot be undone. Are you absolutely sure you want to delete this SuperAdmin user?`,
      actionType: 'delete',
      itemName: this.user.fullName,
      requiresReason: !this.isEventManager,
      confirmButtonText: this.isEventManager ? 'Revoke Role' : 'Delete Permanently',
      cancelButtonText: 'Cancel'
    };

    const confirmDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: true
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result?.confirmed) {
        this.performDelete(result.reason || 'No reason provided');
      }
    });
  }

  private performDelete(reason: string) {
    const request$ = this.isEventManager
      ? this.http.delete(this.baseEndpoint) // revoke role — no body
      : this.http.delete(this.baseEndpoint, { body: { reason } });

    request$.subscribe({
      next: () => {
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: this.isEventManager ? 'Event Manager role revoked' : 'SuperAdmin user deleted permanently' },
          duration: 3000,
        });
        this.dialogRef.close({ updated: true });
      },
      error: (err) => {
        console.error('Delete error:', err);
        this.errorService.openandCloseError(
          err.error?.message || 'Failed to delete user'
        );
      }
    });
  }

  resetPassword() {
    if (!confirm(`Reset password for ${this.user.fullName}? A new temporary password will be sent to their email.`)) {
      return;
    }

    this.http.post(`${this.baseEndpoint}/reset-password`, {})
      .subscribe({
        next: () => {
          this.snackBar.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Password reset successfully. New password sent via email.' },
            duration: 3000,
          });
        },
        error: (err) => {
          console.error('Password reset error:', err);
          this.errorService.openandCloseError(
            err.error?.message || 'Failed to reset password'
          );
        }
      });
  }

  close() {
    this.dialogRef.close();
  }
}